// Genuinely public, unauthenticated catalog reads — GET /public/catalog/*
// (backend/src/routes/publicCatalogRoutes.js), no session required. Used by
// pages/LandingPage.jsx (featured products), pages/CatalogBrowsePage.jsx
// (category tree + product list), and pages/ProductDetailPage.jsx (one
// product's full detail). Every row these return is price/cost-free at the
// repository level (productRepository.listFeatured/listForPortal/getById) —
// nothing here needs its own client-side stripping.

import { useQuery } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

export function useFeaturedProducts() {
  return useQuery({
    queryKey: ['public', 'catalog', 'featured'],
    queryFn: () => apiClient.get('/public/catalog/featured'),
    select: (data) => data.products,
  });
}

export function useCatalogCategories() {
  return useQuery({
    queryKey: ['public', 'catalog', 'categories'],
    queryFn: () => apiClient.get('/public/catalog/categories'),
    select: (data) => data.categories,
  });
}

// The whole active portal catalog (optionally narrowed by a free-text
// search, which the backend runs as a fulltext MATCH, also checked against
// SKU — see productRepository.buildListFilter). The browse screen
// (CatalogBrowsePage.jsx) filters this set by the selected category subtree
// CLIENT-SIDE — the catalog is small (tens of products) and the backend's
// categoryId filter is an exact match that wouldn't include a parent
// category's subcategories anyway. Fetched once and reused as the user
// clicks around the tree; only a new search term refetches.
//
// `enabled` (catalog-picker task): defaults to true, so every existing
// caller (CatalogBrowsePage.jsx, which wants the full catalog even with an
// empty search term) is unaffected. RequestLinesForm.jsx's inline "Add from
// catalog" search passes `enabled: false` until the buyer has actually
// opened that panel AND typed something — without this, merely opening the
// panel would eagerly download the entire catalog over whatever connection
// the buyer has, which is exactly the slow-mobile-data cost portal/
// CLAUDE.md asks this app to avoid.
export function usePortalProducts(search, { enabled = true } = {}) {
  const term = (search || '').trim();
  return useQuery({
    queryKey: ['public', 'catalog', 'products', term],
    queryFn: () => apiClient.get('/public/catalog/products', term ? { search: term } : undefined),
    select: (data) => data.products,
    enabled,
  });
}

// One product's full detail (pages/ProductDetailPage.jsx, /catalog/:id) —
// GET /public/catalog/products/:id, same price/cost-free shape as every
// other public catalog read here. 404 (missing or inactive product) comes
// back as an ApiError with code 'PRODUCT_NOT_FOUND' — the page branches on
// that to show a distinct "no longer available" state rather than the
// generic error banner. `enabled: !!id` guards the brief render where
// useParams() hasn't resolved an id yet.
export function useProductDetail(id) {
  return useQuery({
    queryKey: ['public', 'catalog', 'product', id],
    queryFn: () => apiClient.get(`/public/catalog/products/${id}`),
    select: (data) => data.product,
    enabled: !!id,
  });
}

export default useFeaturedProducts;
